/**
 * Hook Track Service - Serviço para gerenciamento de testes de Hook (Lead Test)
 *
 * Fornece métodos para criar, atualizar, deletar e buscar testes de hook
 */

import { api } from './api';

export interface HookTrackVideo {
  id: string;
  id_video?: string;
  name: string;
  nome?: string;
  thumbnail: string;
  duration?: number;
  views?: number;
  plays?: number;
  trafego?: number;
  percentage?: number;
}

export interface HookTrack {
  id: string;
  id_lead_test?: string;
  name?: string;
  leadTestTitle?: string;
  title?: string;
  userId?: string;
  id_user?: string;
  originalVideoId?: string;
  id_video_original?: string;
  id_video?: string;
  originalVideoThumbnail?: string;
  originalVideoName?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  status?: 'active' | 'paused' | 'completed';
  views?: number;
  videos?: HookTrackVideo[];
}

export interface CreateHookTrackPayload {
  leadTestData: {
    leadTestTitle: string;
    userId: string;
  };
  leadVideosIds: string[];
}

export interface UpdateHookTrackTitlePayload {
  leadTestTitle: string;
}

export interface UpdateHookTrackTrafficPayload {
  videosIds: {
    id_video?: string;
    trafego: number;
  }[];
}

export interface HookTrackMetricsItem {
  leadTest?: any;
  metric: {
    id_video?: string;
    idVideo?: string;
    nomeVideo?: string;
    nome_video?: string;
    thumb?: string;
    thumbnail?: string;
    frame?: string;
    views?: number;
    visualizacoes?: number;
    plays?: number;
    totalPlays?: number;
    play_rate?: number;
    playRate?: number;
    avg_watch_time?: number;
    avgWatchTime?: number;
    completion_rate?: number;
    completionRate?: number;
    conversions?: number;
    trafego_percentual?: number;
    retention_data?: any;
    duration?: number;
    duracao?: number;
  };
}

// Mantém para compatibilidade
export interface HookTrackMetrics {
  id_video: string;
  nome_video: string;
  thumbnail: string;
  views: number;
  plays: number;
  play_rate: number;
  avg_watch_time: number;
  completion_rate: number;
  conversions?: number;
  trafego_percentual: number;
  retention_data?: any;
  duration?: number;
  duracao?: number;
}

export const hookTrackService = {
  /**
   * POST /lead-test/:originalVideoId
   * Cria um novo teste de hook
   */
  createHookTrack: async (
    originalVideoId: string,
    data: CreateHookTrackPayload
  ): Promise<HookTrack> => {
    const response = await api.post(`/lead-test/${originalVideoId}`, data);
    return response;
  },

  /**
   * GET /lead-test/:userId
   * Busca todos os testes de hook de um usuário
   */
  getUserHookTracks: async (userId: string): Promise<HookTrack[]> => {
    const response = await api.get(`/lead-test/${userId}`);
    return Array.isArray(response) ? response : [];
  },

  /**
   * PUT /lead-test/:leadTestId
   * Atualiza o título de um teste de hook
   */
  updateHookTrackTitle: async (
    leadTestId: string,
    data: UpdateHookTrackTitlePayload
  ): Promise<{ message: string }> => {
    const response = await api.put(`/lead-test/${leadTestId}`, data);
    return response;
  },

  /**
   * DELETE /lead-test/:leadTestId
   * Deleta um teste de hook
   */
  deleteHookTrack: async (leadTestId: string): Promise<void> => {
    await api.delete(`/lead-test/${leadTestId}`);
  },

  /**
   * GET /lead-test/:leadTestId/metrics?inicio=YYYY-MM-DD&fim=YYYY-MM-DD
   * Busca métricas de todos os vídeos do teste de hook
   */
  getHookTrackMetrics: async (
    leadTestId: string,
    inicio?: string,
    fim?: string
  ): Promise<HookTrackMetricsItem[]> => {
    const params = new URLSearchParams();
    if (inicio) params.append('inicio', inicio);
    if (fim) params.append('fim', fim);

    const url = `/lead-test/${leadTestId}/metrics${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);

    // A API retorna { message, data }
    if (response && response.data) {
      return Array.isArray(response.data) ? response.data : [];
    }
    return Array.isArray(response) ? response : [];
  },

  /**
   * GET /lead-test/:leadTestId/videos
   * Libera/seleciona um vídeo do teste de hook baseado na distribuição de tráfego
   */
  selectHookTrackVideo: async (leadTestId: string): Promise<any> => {
    const response = await api.get(`/lead-test/${leadTestId}/videos`);
    return response?.data || response;
  },

  /**
   * GET /lead-test/videos/:idVideo
   * Busca teste de hook por ID do vídeo
   */
  getHookTrackByVideoId: async (videoId: string): Promise<HookTrack | null> => {
    const response = await api.get(`/lead-test/videos/${videoId}`);
    return response?.data || response;
  },

  /**
   * PUT /lead-test/:leadTestId/traffic
   * Atualiza a distribuição de tráfego dos vídeos do teste
   */
  updateHookTrackTraffic: async (
    leadTestId: string,
    data: UpdateHookTrackTrafficPayload
  ): Promise<{ message: string; success: boolean; data: any }> => {
    const response = await api.put(`/lead-test/${leadTestId}/traffic`, data);
    return response;
  },
};

export default hookTrackService;
